import { useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Bell, Lightbulb, Megaphone, Network, Sparkles, UserRound } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { usePersonasStore } from '@/stores/personasStore';
import { useTargetsStore, type TargetType } from '@/stores/targetsStore';

const categoryMeta: Record<TargetType, { label: string; icon: typeof Network; description: string }> = {
  followed: { label: 'Followed people', icon: Network, description: 'People this persona follows for context and timely engagement.' },
  inspirator: { label: 'Inspirators', icon: Lightbulb, description: 'Creators and thinkers used to shape content angles.' },
  impress: { label: 'Impress targets', icon: Megaphone, description: 'High-value people this persona wants to reach.' },
};

function formatAlertTypes(notifyOnPost?: boolean, notifyOnComment?: boolean): string {
  const enabled = [notifyOnPost ? 'posts' : null, notifyOnComment ? 'comments' : null].filter(Boolean);
  return enabled.join(' + ') || 'alerts';
}

export function PersonaWorkspacePage() {
  const navigate = useNavigate();
  const { personaId } = useParams<{ personaId: string }>();
  const { personas, fetchPersonas } = usePersonasStore();
  const { targets, fetchTargets } = useTargetsStore();

  useEffect(() => {
    void fetchPersonas();
    void fetchTargets();
  }, [fetchPersonas, fetchTargets]);

  const persona = personas.find((item) => item.id === personaId);
  const personaTargets = useMemo(() => targets.filter((target) => target.persona_id === personaId), [personaId, targets]);

  const counts = useMemo(() => {
    return personaTargets.reduce(
      (acc, target) => {
        acc[target.type ?? 'followed'] += 1;
        return acc;
      },
      { followed: 0, inspirator: 0, impress: 0 },
    );
  }, [personaTargets]);

  const alertsEnabled = personaTargets.filter((target) => target.notify_on_post || target.notify_on_comment).length;

  if (!persona) {
    return (
      <div className="mx-auto max-w-7xl space-y-6">
        <Button
          variant="ghost"
          className="rounded-xl font-black"
          onClick={() => navigate('/personas')}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to personas
        </Button>

        <div className="brutalist-panel-soft rounded-3xl border-dashed p-10 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border-2 border-border bg-secondary">
            <UserRound className="h-7 w-7 text-muted-foreground" />
          </div>
          <p className="mt-4 text-xl font-black tracking-[-0.06em]">Persona workspace unavailable</p>
          <p className="mx-auto mt-2 max-w-md text-sm font-medium text-muted-foreground">
            The persona is still loading or no longer exists. Return to Personas and open a managed workspace from the list.
          </p>
        </div>
      </div>
    );
  }

  const voiceBlocks = [
    {
      label: 'Tone of voice',
      value: persona.voice_summary || persona.style_notes || persona.tone,
    },
    {
      label: 'Audience',
      value: persona.target_audience || 'No audience notes yet. Add target-audience context in this persona to sharpen Studio prompts.',
    },
    {
      label: 'Offer / positioning',
      value: persona.offer || persona.positioning || 'No offer notes yet. Add positioning so generated content has a clear commercial angle.',
    },
  ];
  const contentTopics = persona.content_pillars?.length ? persona.content_pillars : persona.topics ?? [];
  const alertTargets = personaTargets.filter((target) => target.notify_on_post || target.notify_on_comment).slice(0, 6);

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <Button
        variant="ghost"
        className="rounded-xl font-black"
        onClick={() => navigate('/personas')}
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to personas
      </Button>

      <section className="brutalist-panel relative overflow-hidden rounded-3xl p-6 lg:p-8">
        <div className="absolute right-6 top-6 hidden rounded-full border-2 border-border px-3 py-1 text-xs font-black uppercase tracking-[0.2em] text-muted-foreground lg:block">
          {personaTargets.length} network people
        </div>
        <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-start">
          <div className="max-w-4xl">
            <p className="brutalist-label">Persona Workspace</p>
            <div className="mt-3 flex items-start gap-4">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl border-2 border-border bg-primary text-2xl font-black text-primary-foreground shadow-[4px_4px_0_hsl(var(--foreground)/0.2)]">
                {persona.name.charAt(0).toUpperCase() || <UserRound className="h-7 w-7" />}
              </div>
              <div className="min-w-0">
                <h1 className="text-4xl font-black leading-none tracking-[-0.08em] md:text-6xl">{persona.name}</h1>
                <p className="mt-3 text-sm font-black uppercase tracking-[0.18em] text-muted-foreground">
                  {persona.archetype || persona.tone} • {persona.language}
                </p>
              </div>
            </div>
            <p className="mt-5 max-w-3xl text-base font-medium text-muted-foreground md:text-lg">
              {persona.positioning || persona.style_notes || 'Add positioning and style notes to complete this managed persona workspace.'}
            </p>
          </div>

          <div className="flex shrink-0 flex-wrap gap-3">
            <Button
              className="rounded-xl border-2 border-border bg-primary px-5 py-3 text-sm font-black text-primary-foreground shadow-[4px_4px_0_hsl(var(--foreground)/0.22)] transition-transform hover:-translate-y-0.5 dark:shadow-[4px_4px_0_hsl(var(--primary)/0.24)]"
              onClick={() => navigate('/studio')}
            >
              <Sparkles className="mr-2 h-4 w-4" />
              Open Studio
            </Button>
            <Button
              variant="outline"
              className="rounded-xl border-2 border-border px-5 py-3 text-sm font-black"
              onClick={() => navigate('/targets')}
            >
              Manage network
            </Button>
          </div>
        </div>
      </section>

      <section className="grid gap-3 md:grid-cols-4">
        {[
          { label: 'Network people', value: personaTargets.length },
          { label: 'Active alerts', value: alertsEnabled },
          { label: 'Topics', value: persona.topics?.length ?? 0 },
          { label: 'Generated posts', value: persona.posts_generated ?? 0 },
        ].map((metric) => (
          <div key={metric.label} className="brutalist-panel-soft rounded-3xl p-4">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-muted-foreground">{metric.label}</p>
            <p className="mt-2 text-4xl font-black tracking-[-0.08em]">{metric.value}</p>
          </div>
        ))}
      </section>

      <div className="grid gap-4 lg:grid-cols-3">
        <section className="brutalist-panel-soft rounded-3xl p-5 lg:col-span-2">
          <div>
            <p className="brutalist-label">Voice system</p>
            <h2 className="mt-2 text-2xl font-black tracking-[-0.06em]">Voice and content system</h2>
            <p className="mt-2 text-sm font-medium text-muted-foreground">
              Use this as the source of truth for Studio prompts and managed client work.
            </p>
          </div>

          <div className="mt-5 grid gap-3">
            {voiceBlocks.map((block) => (
              <div key={block.label} className="rounded-2xl border-2 border-border bg-background/45 p-4">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-muted-foreground">{block.label}</p>
                <p className="mt-2 text-sm font-medium text-foreground/85">{block.value}</p>
              </div>
            ))}
          </div>

          <div className="mt-5">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-muted-foreground">Content pillars</p>
            {contentTopics.length ? (
              <div className="mt-3 flex flex-wrap gap-2">
                {contentTopics.map((topic) => (
                  <span key={topic} className="rounded-full border border-border bg-secondary px-3 py-1 text-xs font-black">
                    {topic}
                  </span>
                ))}
              </div>
            ) : (
              <p className="mt-2 rounded-2xl border-2 border-dashed border-border bg-background/45 p-4 text-sm font-medium text-muted-foreground">
                No content pillars yet. Add topics to this persona so Studio can produce more focused posts.
              </p>
            )}
          </div>
        </section>

        <section className="brutalist-panel-soft rounded-3xl p-5">
          <div>
            <p className="brutalist-label">Signals</p>
            <h2 className="mt-2 text-2xl font-black tracking-[-0.06em]">Alerts</h2>
            <p className="mt-2 text-sm font-medium text-muted-foreground">Post and comment signals from priority people.</p>
          </div>

          <div className="mt-5 space-y-3">
            {alertTargets.map((target) => (
              <div key={target.id} className="rounded-2xl border-2 border-border bg-background/45 p-3">
                <p className="font-black tracking-[-0.04em]">{target.nickname || target.full_name || 'LinkedIn profile'}</p>
                <p className="mt-1 text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground">
                  <Bell className="mr-1 inline h-3 w-3" />
                  {formatAlertTypes(target.notify_on_post, target.notify_on_comment)} • {target.alert_frequency ?? 'daily'}
                </p>
              </div>
            ))}
            {alertsEnabled === 0 ? (
              <div className="rounded-2xl border-2 border-dashed border-border bg-background/45 p-4 text-sm font-medium text-muted-foreground">
                No alerts enabled yet. Go to Network and enable post or comment alerts for priority people.
              </div>
            ) : null}
          </div>
        </section>
      </div>

      <section className="grid gap-4 md:grid-cols-3">
        {(Object.keys(categoryMeta) as TargetType[]).map((type) => {
          const meta = categoryMeta[type];
          const Icon = meta.icon;
          return (
            <article key={type} className="brutalist-panel-soft flex min-h-full flex-col rounded-3xl p-5 transition-transform hover:-translate-y-1">
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border-2 border-border bg-secondary">
                  <Icon className="h-5 w-5 text-primary" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-xl font-black tracking-[-0.06em]">{meta.label}</h2>
                  <p className="mt-2 text-sm font-medium text-muted-foreground">{meta.description}</p>
                </div>
              </div>

              <div className="mt-5 rounded-2xl border-2 border-border bg-background/45 p-4">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-muted-foreground">People mapped</p>
                <p className="mt-1 text-4xl font-black tracking-[-0.08em]">{counts[type]}</p>
              </div>

              <Link
                className="mt-4 inline-flex w-fit rounded-xl border-2 border-border bg-card px-4 py-2 text-sm font-black transition-transform hover:-translate-y-0.5 hover:bg-secondary"
                to="/targets"
              >
                View people
              </Link>
            </article>
          );
        })}
      </section>
    </div>
  );
}
